// Entrar on Firebase (SPEC-15): email and password, Google, and the
// password reset. The answers the emulator can give (a refused password,
// Google over an existing account, a spent reset code) are proven in
// poweryourflow-functions, test/signIn.test.ts; the rest are Firebase's
// documented codes.
//
// Every sign-in ends with the accountId claim in place. An account without
// it has never been set up (a new Google account, a sign-up cut short), so
// it holds no profile yet: only then is Google's first name stored, and
// saveProfile, which replaces Dados gerais whole, never overwrites one.

import type { User, UserCredential } from "firebase/auth";

import { dataLayer } from "../data";
import { loadFirebase } from "../lib/firebaseModules";
import { LIMITS } from "../schema/types";
import { ensureAccountClaim } from "./accountClaim";
import { codeOf } from "./firebaseError";
import type { GoogleOutcome, SignIn, SignInFailure } from "./signIn";
import { SignInError } from "./signIn";

// Never fetched offline, so a tap without a network leaves the next one
// free to load them (src/lib/firebaseModules.ts).
const load = loadFirebase;

const FAILURES: Readonly<Record<string, SignInFailure>> = {
  // With email enumeration protection on, a wrong password and an unknown
  // email are both auth/invalid-credential; the other two are the answers
  // of a project without it.
  "auth/invalid-credential": "wrong-credentials",
  "auth/wrong-password": "wrong-credentials",
  "auth/user-not-found": "wrong-credentials",
  "auth/invalid-email": "wrong-credentials",
  "auth/too-many-requests": "too-many-attempts",
  "auth/network-request-failed": "offline",
  "app/module-load-failed": "offline",
  "functions/unavailable": "offline",
  "functions/deadline-exceeded": "offline",
  "auth/account-exists-with-different-credential": "account-exists",
  "auth/expired-action-code": "link-invalid",
  "auth/invalid-action-code": "link-invalid",
  "auth/weak-password": "weak-password",
};

/** Closing Google's sheet, or opening a second one over it, is no failure. */
const CANCELLED = new Set(["auth/popup-closed-by-user", "auth/cancelled-popup-request", "auth/user-cancelled"]);

function failure(step: string, error: unknown): SignInError {
  const code = codeOf(error);
  const known = FAILURES[code];
  if (known) return new SignInError(known);
  console.error(`[sign-in] ${step} failed`, { code });
  return new SignInError("failed");
}

/** Google's first name, when it is one saveProfile accepts; otherwise none is stored. */
function googleFirstName(credential: UserCredential, profile: Record<string, unknown> | null | undefined): string | null {
  const given = profile?.["given_name"];
  const firstWord = (credential.user.displayName ?? "").split(" ")[0] ?? "";
  const name = (typeof given === "string" ? given : firstWord).trim();
  return name !== "" && name.length <= LIMITS.FIRST_NAME_MAX ? name : null;
}

/**
 * The claim, or no session at all: when the claim cannot be set, the
 * session just opened is closed again, so a phone showing an error is
 * never left signed in. The next sign-in sets the claim.
 */
async function claimOrSignOut([{ app, auth }, authApi]: Awaited<ReturnType<typeof load>>, user: User): Promise<boolean> {
  try {
    return await ensureAccountClaim(app, user);
  } catch (error) {
    await authApi.signOut(auth).catch(() => {});
    throw error;
  }
}

export const firebaseSignIn: SignIn = {
  async withPassword(email, password) {
    try {
      const firebase = await load();
      const [{ auth }, authApi] = firebase;
      const { user } = await authApi.signInWithEmailAndPassword(auth, email, password);
      await claimOrSignOut(firebase, user);
    } catch (error) {
      throw failure("password", error);
    }
  },

  async withGoogle(): Promise<GoogleOutcome> {
    // The sheet is Google's page; without a network it opens blank and
    // never answers.
    if (!navigator.onLine) throw new SignInError("offline");
    try {
      const firebase = await load();
      const [{ auth }, authApi] = firebase;
      // A popup rather than a redirect: the redirect reads Firebase's own
      // domain in a frame, which Safari and Chrome partition away from an
      // app served on another domain (poweryourflow-ops/docs/decisions.md).
      const credential = await authApi.signInWithPopup(auth, new authApi.GoogleAuthProvider());
      if (!(await claimOrSignOut(firebase, credential.user))) return "assessments";

      const firstName = googleFirstName(credential, authApi.getAdditionalUserInfo(credential)?.profile);
      if (firstName) {
        // The session and the claim are there: a lost first name is no
        // reason to keep her out. Dados gerais is where it is asked again.
        await dataLayer()
          .saveProfile({ firstName })
          .catch((error: unknown) => console.error("[sign-in] Google first name not stored", { code: codeOf(error) }));
      }
      return "profileOnboarding";
    } catch (error) {
      if (CANCELLED.has(codeOf(error))) return "cancelled";
      throw failure("google", error);
    }
  },

  async sendPasswordReset(email) {
    try {
      const [{ auth }, authApi] = await load();
      await authApi.sendPasswordResetEmail(auth, email);
    } catch (error) {
      // Only a project without enumeration protection answers this; the
      // screen must still say the same as for an email with an account.
      if (codeOf(error) === "auth/user-not-found") return;
      throw failure("reset email", error);
    }
  },

  async confirmPasswordReset(code, password) {
    try {
      const [{ auth }, authApi] = await load();
      await authApi.confirmPasswordReset(auth, code, password);
    } catch (error) {
      throw failure("new password", error);
    }
  },

  async signOut() {
    try {
      const [{ auth }, authApi] = await load();
      await authApi.signOut(auth);
    } catch (error) {
      console.error("[sign-in] sign-out after a late answer failed", { code: codeOf(error) });
    }
  },
};
